import * as React from "react";
import { Box, Card, Stack, Typography } from "@mui/material";

import type { LeadPreview } from "@/hooks/api/use-lead-finder";

export interface AudienceSummaryProps {
	total: number;
	preview: LeadPreview[];
	/** Companies with the news the search asked for, when it asked for some. */
	newsCompanies?: number;
}

/** An estimate, rounded so it does not read as exact. */
const about = (value: number) =>
	`≈ ${(value < 100 ? Math.round(value) : Math.round(value / 10) * 10).toLocaleString()}`;

/**
 * How many people the search matches, and how many of them could become leads. Only the total is ZoomInfo's
 * count; who may be called and who is new are estimated from the free preview, so they read "≈".
 */
export function AudienceSummary({ total, preview, newsCompanies }: AudienceSummaryProps): React.JSX.Element {
	const sample = preview.length;
	const callable = sample ? preview.filter((lead) => lead.callable).length / sample : 1;
	const fresh = sample ? preview.filter((lead) => lead.callable && !lead.inAccount).length / sample : 1;
	const stages = [
		{
			label: "Can be called",
			value: about(total * callable),
			share: callable,
			color: "var(--mui-palette-primary-main)",
		},
		{ label: "New to you", value: about(total * fresh), share: fresh, color: "var(--mui-palette-primary-400)" },
	];

	return (
		<Card sx={{ borderRadius: "16px", p: 3, display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}>
			<Box sx={{ flex: "0 0 auto" }}>
				<Typography variant="body2" color="text.secondary">
					People who fit
				</Typography>
				<Typography sx={{ fontSize: 40, lineHeight: "48px", fontWeight: 700, letterSpacing: "-0.02em" }}>
					{total.toLocaleString()}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					{newsCompanies === undefined
						? "free to search"
						: `at ${newsCompanies.toLocaleString()} ${newsCompanies === 1 ? "company" : "companies"} in the news for it`}
				</Typography>
			</Box>
			<Stack spacing={1} sx={{ flex: "1 1 340px", minWidth: 0 }} aria-label="Of those">
				{stages.map((stage) => (
					<Box
						key={stage.label}
						sx={{ display: "grid", gridTemplateColumns: "140px minmax(0, 1fr) 72px", gap: 1.5, alignItems: "center" }}
					>
						<Typography variant="body2" color="text.secondary">
							{stage.label}
						</Typography>
						<Box
							sx={{
								height: 10,
								borderRadius: 999,
								bgcolor: "var(--mui-palette-background-level1)",
								overflow: "hidden",
							}}
						>
							<Box
								sx={{
									height: "100%",
									width: `${Math.max(stage.share, 0.02) * 100}%`,
									bgcolor: stage.color,
									borderRadius: 999,
								}}
							/>
						</Box>
						<Typography variant="subtitle2" sx={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
							{stage.value}
						</Typography>
					</Box>
				))}
				{sample > 0 && (
					<Typography variant="caption" color="text.secondary">
						≈ estimated from the {sample} people in the preview
					</Typography>
				)}
			</Stack>
		</Card>
	);
}
